import {useEffect, useMemo, useState} from 'react';
import {LicenseInfo} from '@mui/x-license-pro';
import {jwtDecode} from 'jwt-decode';
import {environment} from '@shared/config/environment';
import InitSharedRepo from '../../shared';
import AppInitializer from './AppInitializer';
import {MsalProvider} from '@azure/msal-react';
import {PublicClientApplication, EventType} from '@azure/msal-browser';
import AuthUtils from './utils/AuthUtils';
import FormConstants from '@shared/constants/FormConstants';
import {MotionLazy} from './minimal-components/animate/motion-lazy';
import {LocalizationProvider} from './locales';
import {SettingsDrawer, SettingsProvider} from './minimal-components/settings';
import ThemeProvider from './theme';
import {SnackbarProvider, useSnackbar} from 'notistack';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import {decrypt} from '@shared/utils/LocalStorage';
import {setSession} from './auth/context/jwt/utils';
import {fetchEnvironmentConfig} from './utils/helperUtils';
import {SplashScreen} from './minimal-components/loading-screen';
import GlobalChatWidget from '@components/enterprise-search/Chat_app/GlobalChatWidget';
import {BASE_ROUTE} from './routes/BaseRoute';
import {PUBLIC_ROUTES} from './routes/RouteConstants';

const App = (): JSX.Element => {
  const [msalAuthConfig, setMsalAuthConfig] = useState<any | null>(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  // const [permissionExisted,setPermissionExisted]=useState(false)
  const {enqueueSnackbar} = useSnackbar();
  const permissions = AuthUtils.getPermissions();
  const {
    appState: {authConfig}
  } = useAppContext();

  // Clear token on app init if invalid or not present
  useEffect(() => {
    if (AuthUtils.getAuthToken()) {
      const userData: any = jwtDecode(AuthUtils.getAuthToken() || '');
      if (userData?.user?.type === FormConstants.EPIC_TERMS.BASE) {
        AuthUtils.clearAuthToken();
      }
    } else {
      setSession(null);
    }
  }, []);

  // Try local storage first, else fetch config
  useEffect(() => {
    const authTempObj = decrypt(localStorage.getItem('authConfig')) || null;
    const authConfigObj: any = authTempObj
      ? JSON.parse(authTempObj)
      : environment?.msalConfig?.auth?.[0];

    if (authConfigObj?.clientId) {
      setMsalAuthConfig(authConfigObj);
      setIsConfigLoaded(true);
    } else {
      fetchEnvironmentConfig().then(env => {
        setMsalAuthConfig(env?.msalConfig?.auth?.[0] || null);
        setIsConfigLoaded(true);
      });
    }
  }, [authConfig]);

  // One-time setup
  useEffect(() => {
    LicenseInfo.setLicenseKey(environment.muiLicenseKey);
    InitSharedRepo.init({
      baseUrl: environment.baseUrl,
      reqTimeout: environment.reqTimeout
    });
  }, []);

  // Network change notifications
  useEffect(() => {
    const handleOnline = () =>
      enqueueSnackbar('You are now online', SnackBarConfig.getSuccess());
    const handleOffline = () =>
      enqueueSnackbar('You are now offline', SnackBarConfig.getWarning());
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [enqueueSnackbar]);

  // Memoize MSAL instance
  const msalInstance = useMemo(() => {
    if (!msalAuthConfig?.clientId) return null;
    return new PublicClientApplication({
      ...environment.msalConfig,
      auth: msalAuthConfig
    });
  }, [msalAuthConfig]);

  const loginPath = `/${BASE_ROUTE}/${PUBLIC_ROUTES.LOGIN}`;
  const loginUrl = `${window.location.origin}${loginPath}`;

  // Handle MSAL redirect login + set active account
  useEffect(() => {
    if (!msalInstance) return;

    const initMsal = async () => {
      try {
        await msalInstance.initialize(); // Required before handleRedirectPromise
        const response = await msalInstance.handleRedirectPromise();

        if (response) {
          //console.log('MSAL Redirect Response:', response);
          const {idToken, account} = response;

          if (idToken && account) {
            msalInstance.setActiveAccount(account);
            AuthUtils.setAzureRedirected(true);
            if (window.location.pathname !== loginPath) {
              window.location.replace(loginUrl);
            }
          }
        }
      } catch (error) {
        console.error('MSAL Redirect Error:', error);
      }
    };

    initMsal();
  }, [msalInstance]);

  // Listen to login events for fallback login flow
  useEffect(() => {
    if (!msalInstance) return;

    const callbackId = msalInstance.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        const {idToken, account} = event?.payload || {};
        if (idToken && account) {
          msalInstance.setActiveAccount(account);
          AuthUtils.setAzureRedirected(true);
          if (window.location.pathname !== loginPath) {
            window.location.replace(loginUrl);
          }
        }
      }
    });

    return () => {
      if (callbackId) msalInstance.removeEventCallback(callbackId);
    };
  }, [loginPath, loginUrl, msalInstance]);

  if (!isConfigLoaded || !msalInstance) {
    return (
      <div>
        <SplashScreen />
      </div>
    );
  }

  const permissionExisted = Array.isArray(permissions)
    ? permissions?.some(
        (ele: any) =>
          ele.object === 'enterprise_search' &&
          ele.function === 'chat-widget-view'
      )
    : false;

  return (
    <MsalProvider instance={msalInstance}>
      <LocalizationProvider>
        <SettingsProvider
          defaultSettings={{
            themeMode: 'light', // 'light' | 'dark'
            themeDirection: 'ltr', //  'rtl' | 'ltr'
            themeContrast: 'bold', // 'default' | 'bold'
            themeLayout: 'vertical', // 'vertical' | 'horizontal' | 'mini'
            themeColorPresets: 'default', // 'default' | 'cyan' | 'purple' | 'blue' | 'orange' | 'red'
            themeStretch: true
          }}>
          <ThemeProvider>
            <MotionLazy>
              <SnackbarProvider>
                <SettingsDrawer />
                <AppInitializer />
                {permissionExisted &&
                  window.location.pathname.includes('/bytepad/module') && (
                    <GlobalChatWidget /> // Remove isVisible prop
                  )}
              </SnackbarProvider>
            </MotionLazy>
          </ThemeProvider>
        </SettingsProvider>
      </LocalizationProvider>
    </MsalProvider>
  );
};
export default App;

